import React, { useEffect, useMemo, useRef, useState } from 'react';

import CartoonSelect from './CartoonSelect';
import { buildAntigravityTask } from './antigravityTask';

const API = '/api';
const VIEW_COPY = {
  extract: { number: '01', title: 'Từ hồ sơ giấy đến dữ liệu số.', description: 'Chọn nguồn tài liệu, áp dụng mẫu nhận dạng và nhận kết quả Excel.', label: 'Số hóa hồ sơ', icon: 'scan' },
  catalog: { number: '02', title: 'Mỗi loại tài liệu, một mẫu chuẩn.', description: 'Thiết lập thông tin cần lấy một lần để dùng lại cho những lần số hóa sau.', label: 'Mẫu số hóa', icon: 'catalog' },
  ocr: { number: '03', title: 'Năng lực nhận dạng của bạn.', description: 'Kết nối tài khoản AI, kiểm tra hạn mức và lựa chọn mô hình đọc tài liệu.', label: 'Kết nối AI', icon: 'settings' },
  account: { number: '04', title: 'Không gian của bạn.', description: 'Thông tin đăng nhập và bảo mật tài khoản Số hóa Tường Trung.', label: 'Tài khoản', icon: 'user' },
};
const DEFAULT_SYSTEM_PROMPT = `Bạn là hệ thống đọc ảnh và trích xuất dữ liệu có cấu trúc từ tài liệu tiếng Việt.

- Đọc trực tiếp toàn bộ ảnh hoặc các trang được cung cấp.
- Chỉ lấy thông tin nhìn thấy; không suy đoán hoặc tự bổ sung.
- Không tìm thấy hoặc không đọc chắc chắn: dùng chuỗi rỗng "".
- Chỉ trả về một JSON object hợp lệ, không markdown hoặc giải thích.
- Dùng chính xác tên trường được yêu cầu; không thêm trường khác; mọi giá trị là chuỗi.`;
const DEFAULT_USER_PROMPT = `Đọc ảnh tài liệu đính kèm và trích xuất các trường sau:

{{field_instructions}}

Trả về JSON gồm chính xác các trường trên.`;

function emptyType(groupId) {
  return {
    id: `type_${Date.now()}`,
    group_id: groupId || '',
    name: 'Loại tài liệu mới',
    description: '',
    system_prompt: DEFAULT_SYSTEM_PROMPT,
    user_prompt: DEFAULT_USER_PROMPT,
    extraction_method: 'cockpit_ocr',
    pdf_extract_pages: 1,
    fields: [],
  };
}

function App() {
  const [confirmation, setConfirmation] = useState(null);
  const confirmationResolver = useRef(null);
  function askConfirmation(message) {
    return new Promise((resolve) => { confirmationResolver.current = resolve; setConfirmation(message); });
  }
  function finishConfirmation(accepted) {
    setConfirmation(null);
    confirmationResolver.current?.(accepted);
    confirmationResolver.current = null;
  }
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: 'admin', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginPending, setLoginPending] = useState(false);
  const [lightOn, setLightOn] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const pullStart = useRef(null);
  const pullDragged = useRef(false);
  const [catalogTab, setCatalogTab] = useState('templates');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [activeView, setActiveView] = useState('extract');
  const [config, setConfig] = useState({ groups: [], document_types: [] });
  const [savedConfig, setSavedConfig] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const savingConfig = useRef(false);
  const groupsDirty = savedConfig !== null && JSON.stringify(config.groups) !== JSON.stringify(savedConfig.groups);
  const templatesDirty = savedConfig !== null && JSON.stringify(config.document_types) !== JSON.stringify(savedConfig.document_types);
  const configDirty = savedConfig !== null && JSON.stringify(config) !== JSON.stringify(savedConfig);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [folderInfo, setFolderInfo] = useState(null);
  const [tuSoFieldId, setTuSoFieldId] = useState('');
  const [layoutMode, setLayoutMode] = useState('direct');
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState('');
  const [pausedJob, setPausedJob] = useState(null);
  const [resuming, setResuming] = useState(false);
  const jobSource = useRef(null);
  useEffect(() => () => jobSource.current?.close(), []);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [message, setMessage] = useState('');
  const [health, setHealth] = useState(null);
  const [ocrSettings, setOcrSettings] = useState(null);
  const [ocrAccounts, setOcrAccounts] = useState([]);
  const [ocrSaving, setOcrSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const selectedType = useMemo(
    () => config.document_types.find((item) => item.id === selectedTypeId) || config.document_types[0],
    [config.document_types, selectedTypeId]
  );
  const fields = selectedType?.fields || [];
  const selectedGroup = config.groups.find((group) => group.id === selectedGroupId) || config.groups[0];
  const ocrModels = useMemo(() => {
    if (!ocrSettings) return [];
    const selectedAccount = ocrAccounts.find((account) => account.key === ocrSettings.accountId);
    if (!selectedAccount) return [];
    const source = ocrSettings.rotateAccounts
      ? ocrAccounts.filter((account) => account.enabled !== false && account.provider === selectedAccount.provider).flatMap((account) => account.models || [])
      : selectedAccount.models || [];
    const unique = new Map();
    source.forEach((model) => {
      const key = `${model.quotaGroupId || ''}:${model.id}`;
      if (!unique.has(key)) unique.set(key, model);
    });
    return [...unique.values()].sort((a, b) => Number(b.id === 'gemma-4-31b-it') - Number(a.id === 'gemma-4-31b-it') || String(a.displayName || a.id).localeCompare(String(b.displayName || b.id), 'vi', { numeric: true }));
  }, [ocrAccounts, ocrSettings]);
  const ocrReasoningEfforts = useMemo(() => {
    if (/^(claude|gemini|cockpit):/.test(ocrSettings?.accountId || '')) return [];
    const model = ocrModels.find((item) => item.id === ocrSettings?.model);
    return model?.reasoningEfforts?.length ? model.reasoningEfforts : ['low', 'medium', 'high', 'xhigh', 'ultra'];
  }, [ocrModels, ocrSettings?.model, ocrSettings?.accountId]);
  const progressEvent = events.find((item) => ['progress', 'paused', 'start'].includes(item.type));
  const completeEvent = events.find((item) => item.type === 'complete');
  const selectedImageCount = layoutMode === 'person_folders' ? folderInfo?.personFolderImageCount || 0 : folderInfo?.dossierFolderImageCount || 0;
  const totalImages = completeEvent?.payload?.totalImages || progressEvent?.payload?.totalImages || selectedImageCount;
  const currentImage = completeEvent ? totalImages : progressEvent?.payload?.currentImage || 0;
  const progressPercent = totalImages ? Math.round((currentImage / totalImages) * 100) : 0;

  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    if (!authenticated) return undefined;
    const timer = setInterval(loadHealth, 30000);
    return () => clearInterval(timer);
  }, [authenticated]);

  useEffect(() => {
    if (selectedType && selectedType.id !== selectedTypeId) setSelectedTypeId(selectedType.id);
  }, [selectedType, selectedTypeId]);

  useEffect(() => {
    if (ocrSettings && ocrModels.length && !ocrModels.some((model) => model.id === ocrSettings.model)) {
      setOcrSettings((current) => ({ ...current, model: ocrModels[0].id }));
    }
  }, [ocrModels, ocrSettings]);

  useEffect(() => {
    if (!authenticated || !user || savedConfig === null) return;
    try {
      const key = `tt-config-draft:${user.username}`;
      if (configDirty) sessionStorage.setItem(key, JSON.stringify(config));
      else sessionStorage.removeItem(key);
    } catch (_error) { /* Storage may be unavailable; in-app unsaved warnings remain active. */ }
  }, [authenticated, user, savedConfig, config, configDirty]);

  async function changeView(view) {
    if (view === activeView) return;
    if (configDirty && !(await askConfirmation('Mẫu số hóa có thay đổi chưa lưu. Chuyển trang? Bản chỉnh sửa vẫn được giữ trong phiên này; quay lại Mẫu số hóa để lưu.'))) return;
    setActiveView(view);
  }

  async function apiFetch(url, options = {}) {
    const response = await fetch(`${API}${url}`, {
      ...options,
      credentials: 'same-origin',
      headers: {
        ...(options.headers || {}),
      },
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : {};
    if (!response.ok) throw new Error(data.message || 'Request failed');
    return data;
  }

  async function bootstrap() {
    try {
      const me = await apiFetch('/auth/me');
      setUser(me.user);
      setAuthenticated(true);
      await Promise.all([loadConfig(me.user.username), loadHealth(), loadOcrSettings()]);
      await restoreJob();
    } catch (_error) {
      setAuthenticated(false);
      setUser(null);
    }
  }

  async function login(event) {
    event.preventDefault();
    if (loginPending) return;
    setLoginPending(true);
    setLoginError('');
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Không đăng nhập được');
      setUser(data.user);
      setAuthenticated(true);
      await Promise.all([loadConfig(data.user.username), loadHealth(), loadOcrSettings()]);
      await restoreJob();
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setLoginPending(false);
    }
  }

  async function logout() {
    if (configDirty && !(await askConfirmation('Bạn chưa lưu thay đổi nhóm hoặc mẫu số hóa. Đăng xuất sẽ bỏ các thay đổi này. Vẫn đăng xuất?'))) return;
    try { sessionStorage.removeItem(`tt-config-draft:${user?.username}`); } catch (_error) {}
    fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'same-origin' }).catch(() => {});
    jobSource.current?.close(); setRunning(false); setPausedJob(null);
    setAuthenticated(false);
    setUser(null);
    setSavedConfig(null);
  }

  async function loadConfig(username) {
    const data = await apiFetch('/pipeline/config');
    let initial = data;
    try {
      const draft = JSON.parse(sessionStorage.getItem(`tt-config-draft:${username}`) || 'null');
      if (Array.isArray(draft?.groups) && Array.isArray(draft?.document_types)) {
        initial = draft;
        setMessage('Đã khôi phục bản nháp trong phiên này. Kiểm tra và lưu khi sẵn sàng.');
      }
    } catch (_error) {}
    setConfig(initial);
    setSavedConfig(data);
    if (data.document_types[0]) setSelectedTypeId(data.document_types[0].id);
  }

  async function loadFolder(pathValue = folderPath) {
    const query = pathValue ? `?path=${encodeURIComponent(pathValue)}` : '';
    const data = await apiFetch(`/pipeline/folders${query}`);
    setFolderInfo(data);
    setFolderPath(data.relativePath || '');
  }

  function breadcrumbParts() {
    const parts = String(folderPath || '').split('/').filter(Boolean);
    return [
      { label: '/scan', path: '' },
      ...parts.map((part, index) => ({
        label: part,
        path: parts.slice(0, index + 1).join('/'),
      })),
    ];
  }

  async function loadHealth() {
    try {
      const response = await fetch(`${API}/health`);
      const data = await response.json();
      setHealth(data);
    } catch (error) {
      setHealth({ status: 'error', error: error.message });
    }
  }

  async function loadOcrSettings() {
    try {
      const [settingsResponse, codexResponse, antigravityResponse, claudeResponse, geminiResponse, cockpitResponse] = await Promise.all([
        fetch('/admin/ocr-settings'),
        fetch('/admin/accounts'),
        fetch('/admin/antigravity/accounts'),
        fetch('/admin/claude/accounts'),
        fetch('/admin/gemini/accounts'),
        fetch('/admin/cockpit/accounts'),
      ]);
      if (!settingsResponse.ok) throw new Error('Không đọc được cài đặt Nhận dạng tài liệu');
      const settings = await settingsResponse.json();
      const codex = codexResponse.ok ? await codexResponse.json() : { accounts: [] };
      const antigravity = antigravityResponse.ok ? await antigravityResponse.json() : { accounts: [] };
      const claude = claudeResponse.ok ? await claudeResponse.json() : { accounts: [] };
      const gemini = geminiResponse.ok ? await geminiResponse.json() : { accounts: [] };
      const cockpit = cockpitResponse.ok ? await cockpitResponse.json() : { accounts: [] };
      setOcrSettings(settings);
      setOcrAccounts([
        ...(codex.accounts || []).map((account) => ({ ...account, key: account.id, provider: 'codex' })),
        ...(antigravity.accounts || []).map((account) => ({ ...account, key: `antigravity:${account.id}`, provider: 'antigravity' })),
        ...(claude.accounts || []).map((account) => ({ ...account, key: `claude:${account.id}`, provider: 'claude' })),
        ...(gemini.accounts || []).map((account) => ({ ...account, key: `gemini:${account.id}`, provider: 'gemini' })),
        ...(cockpit.accounts || []).map((account) => ({ ...account, key: `cockpit:${account.id}`, provider: 'cockpit' })),
      ]);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function saveOcrSettings() {
    if (!ocrSettings) return false;
    setOcrSaving(true);
    try {
      const normalizedSettings = {
        ...ocrSettings,
        retries: clampNumber(ocrSettings.retries, 1, 10, 3),
        maxImageWidth: clampNumber(ocrSettings.maxImageWidth, 320, 10000, 1200),
      };
      const response = await fetch('/admin/ocr-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizedSettings),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Không lưu được cài đặt OCR');
      setOcrSettings(data);
      setMessage('Đã áp dụng cài đặt OCR');
      setTimeout(() => setMessage(''), 1800);
      return true;
    } catch (error) {
      setMessage(error.message);
      return false;
    } finally {
      setOcrSaving(false);
    }
  }

  async function saveConfig(scope) {
    if (savingConfig.current || !savedConfig) return;
    const snapshot = config;
    const savedGroups = new Set(savedConfig.groups.map(group => group.id));
    if (scope === 'templates' && config.document_types.some(type => type.group_id && !savedGroups.has(type.group_id))) {
      setMessage('Mẫu đang dùng nhóm mới chưa lưu. Hãy sang Nhóm tài liệu và lưu nhóm trước, rồi quay lại lưu mẫu.');
      return;
    }
    const groupIds = new Set(config.groups.map(group => group.id));
    const submitted = scope === 'groups'
      ? { ...savedConfig, groups: config.groups, document_types: savedConfig.document_types.map(type => type.group_id && !groupIds.has(type.group_id) ? { ...type, group_id: '' } : type) }
      : { ...savedConfig, document_types: config.document_types };
    savingConfig.current = true;
    setConfigSaving(true);
    try {
      const data = await apiFetch('/pipeline/config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitted),
      });
      setSavedConfig(data);
      // Update only the saved scope; preserve the other tab and any in-flight edits.
      setConfig(current => {
        const key = scope === 'groups' ? 'groups' : 'document_types';
        return JSON.stringify(current[key]) === JSON.stringify(snapshot[key])
          ? { ...current, [key]: data[key] } : current;
      });
      setMessage(scope === 'groups' ? 'Đã lưu nhóm. Các chỉnh sửa mẫu chưa lưu vẫn được giữ riêng.' : 'Đã lưu mẫu. Các chỉnh sửa nhóm chưa lưu vẫn được giữ riêng.');
    } catch (error) {
      setMessage(`Chưa lưu được: ${error.message}. Bản chỉnh sửa vẫn được giữ, hãy thử lại.`);
    } finally {
      savingConfig.current = false;
      setConfigSaving(false);
    }
  }

  function updateGroup(index, patch) {
    setConfig((current) => {
      const groups = [...current.groups];
      groups[index] = { ...groups[index], ...patch };
      return { ...current, groups };
    });
  }

  function addGroup() {
    const id = `group_${Date.now()}`;
    setSelectedGroupId(id);
    setConfig((current) => ({
      ...current,
      groups: [...current.groups, { id, name: `Nhóm ${current.groups.length + 1}`, description: '' }],
    }));
  }

  async function removeGroup(index) {
    if (!(await askConfirmation('Xóa nhóm này? Các loại tài liệu trong nhóm sẽ chuyển về chưa phân nhóm.'))) return;
    setConfig((current) => {
      const removed = current.groups[index];
      return {
        groups: current.groups.filter((_, i) => i !== index),
        document_types: current.document_types.map((type) => type.group_id === removed.id ? { ...type, group_id: '' } : type),
      };
    });
  }

  function addType() {
    const type = emptyType(config.groups[0]?.id);
    setConfig((current) => ({ ...current, document_types: [...current.document_types, type] }));
    setSelectedTypeId(type.id);
  }

  function updateType(patch) {
    if (!selectedType) return;
    setConfig((current) => ({
      ...current,
      document_types: current.document_types.map((item) => item.id === selectedType.id ? { ...item, ...patch } : item),
    }));
  }

  async function removeType() {
    if (!selectedType) return;
    if (!(await askConfirmation(`Xóa loại tài liệu "${selectedType.name}" và toàn bộ trường dữ liệu của loại này?`))) return;
    setConfig((current) => {
      const nextTypes = current.document_types.filter((type) => type.id !== selectedType.id);
      setSelectedTypeId(nextTypes[0]?.id || '');
      return { ...current, document_types: nextTypes };
    });
  }

  function duplicateType() {
    if (!selectedType) return;
    const copyId = `type_${Date.now()}`;
    const copy = {
      ...selectedType,
      id: copyId,
      name: `${selectedType.name} - bản sao`,
      fields: fields.map((field, index) => ({
        ...field,
        id: `field_${Date.now()}_${index}`,
      })),
    };
    setConfig((current) => ({ ...current, document_types: [...current.document_types, copy] }));
    setSelectedTypeId(copyId);
  }

  function addField() {
    updateType({
      fields: [
        ...fields,
        { id: `field_${Date.now()}`, name: 'Trường mới', field_type: 'text', instruction: '' },
      ],
    });
  }

  function updateField(index, patch) {
    const nextFields = [...fields];
    nextFields[index] = { ...nextFields[index], ...patch };
    updateType({ fields: nextFields });
  }

  async function removeField(index) {
    if (!(await askConfirmation(`Xóa trường "${fields[index]?.name || ''}"?`))) return;
    updateType({ fields: fields.filter((_, i) => i !== index) });
  }

  function moveField(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    const nextFields = [...fields];
    const [moved] = nextFields.splice(index, 1);
    nextFields.splice(targetIndex, 0, moved);
    updateType({ fields: nextFields });
  }

  async function startJob() {
    if (!selectedType) return;
    setEvents([]);
    setDownloadUrl('');
    setRunning(true);
    setMessage('');
    try {
      if (!(await saveOcrSettings())) throw new Error('Cần lưu được cài đặt OCR trước khi chạy');
      const data = await apiFetch('/pipeline/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderPath, documentTypeId: selectedType.id, tuSoFieldId, layoutMode }),
      });
      connectJob(data, folderPath);
    } catch (error) {
      setRunning(false);
      setMessage(error.message);
    }
  }

  function exportAntigravityTask() {
    try {
      const rules = fields.filter(field => field.field_type !== 'document_page_count').map(formatFieldRule).join('\n');
      const task = buildAntigravityTask({
        documentType: {
          ...selectedType,
          system_prompt: renderPromptPreview(selectedType?.system_prompt?.trim() || DEFAULT_SYSTEM_PROMPT, rules),
          user_prompt: renderPromptPreview(selectedType?.user_prompt?.trim() || DEFAULT_USER_PROMPT, rules),
        },
        folderInfo, layoutMode, tuSoFieldId, health, draft: templatesDirty,
      });
      const blob = new Blob([JSON.stringify(task, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `antigravity-${selectedType.name.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').slice(0, 80)}.json`;
      document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000); }
      setMessage('Đã xuất cấu hình Antigravity theo lựa chọn hiện tại. Mở file JSON cùng skill và chỉ định thư mục nguồn mà Antigravity truy cập được.');
    } catch (error) { setMessage(error.message); }
  }

  function connectJob(data, sourceFolder = folderPath) {
      jobSource.current?.close();
      setJobId(data.jobId);
      setPausedJob(null);
      const source = new EventSource(data.streamUrl);
      jobSource.current = source;
      let terminalReceived = false;
      let connectionErrors = 0;
      const push = (type) => (event) => {
        const raw = typeof event?.data === 'string' ? event.data.trim() : '';
        if (!raw || raw === 'undefined' || raw === 'null') return;
        let payload;
        try {
          payload = JSON.parse(raw);
        } catch (_error) {
          setMessage(`Bỏ qua phản hồi SSE không hợp lệ (${type})`);
          return;
        }
        connectionErrors = 0;
        setEvents((current) => {
          const next = type === 'progress' ? current.filter((item) => item.type !== 'progress') : current;
          if (type === 'imageSuccess') return next;
          return [{ type, payload, at: new Date().toLocaleTimeString('vi-VN') }, ...next].slice(0, 30);
        });
        if (type === 'excelSaved') {
          const url = `/api/pipeline/jobs/${data.jobId}/excel`;
          setDownloadUrl(url);
        }
        if (type === 'complete' || type === 'stopped') setPausedJob(null);
        if (type === 'paused') { setPausedJob({ ...payload, jobId: data.jobId, folderPath: sourceFolder }); setMessage(payload.message); }
        if (type === 'complete' || type === 'stopped' || type === 'error' || type === 'paused') {
          terminalReceived = true;
          setRunning(false);
          source.close();
          loadFolder(sourceFolder).catch(error => setMessage(error.message));
        }
      };
      ['start', 'progress', 'imageError', 'excelSaved', 'complete', 'stopped', 'paused'].forEach((eventName) => {
        source.addEventListener(eventName, push(eventName));
      });
      source.addEventListener('jobError', push('error'));
      source.onerror = () => {
        if (terminalReceived) return;
        connectionErrors += 1;
        if (connectionErrors >= 5 || source.readyState === EventSource.CLOSED) {
          setRunning(false);
          setMessage('Kết nối tiến trình gián đoạn. Tải lại trang để khôi phục trạng thái và tiếp tục.');
          source.close();
          return;
        }
        setMessage(`Kết nối tiến trình tạm gián đoạn, đang thử lại (${connectionErrors}/5)…`);
      };
  }

  async function restoreJob() {
    try {
      const jobs = await apiFetch('/pipeline/jobs');
      const job = jobs.find(item => item.status === 'running' || item.status === 'queued') || jobs.find(item => item.status === 'paused');
      if (!job) { await loadFolder(''); return; }
      await loadFolder(job.folder_path);
      setSelectedTypeId(job.document_type_id || ''); setLayoutMode(job.layout_mode || 'direct');
      setRunning(job.status !== 'paused');
      connectJob({ jobId: job.id, streamUrl: `/api/pipeline/jobs/${job.id}/stream` }, job.folder_path);
    } catch (error) { setMessage(error.message); }
  }

  async function resumeJob() {
    if (!pausedJob || resuming) return;
    setResuming(true); setMessage('');
    try {
      if (!(await saveOcrSettings())) throw new Error('Cần lưu được cài đặt OCR trước khi tiếp tục');
      const data = await apiFetch(`/pipeline/jobs/${pausedJob.jobId}/resume`, { method: 'POST' });
      setEvents(current => current.filter(item => !['paused', 'complete', 'error'].includes(item.type)));
      await loadFolder(pausedJob.folderPath);
      setRunning(true); connectJob(data, pausedJob.folderPath);
    } catch (error) { setMessage(error.message); }
    finally { setResuming(false); }
  }

  async function stopJob() {
    if (!jobId) return;
    if (pausedJob && !(await askConfirmation('Kết thúc tiến trình đã lưu? Các ảnh giữ nguyên, nhưng kết quả trong tiến trình này sẽ bị bỏ và chưa xuất Excel.'))) return;
    try {
      await apiFetch(`/pipeline/jobs/${jobId}/stop`, { method: 'POST' });
      if (pausedJob) { setPausedJob(null); setEvents(current => [{ type: 'stopped', payload: {}, at: '' }, ...current]); }
    } catch (error) { setMessage(error.message); }
  }

  async function changePassword(event) {
    event.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage('Mật khẩu xác nhận không khớp');
      return;
    }
    try {
      const data = await apiFetch('/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setMessage(data.message || 'Đã đổi mật khẩu');
    } catch (error) {
      setMessage(error.message);
    }
  }

  if (!authenticated || !user) {
    return (
      <div className={`login-shell lamp-login ${lightOn ? 'is-lit' : ''} ${pullDistance > 0 ? 'is-pulling' : ''}`}>
        <div className="login-signature">SỐ HÓA TƯỜNG TRUNG<span>GIẢI PHÁP SỐ HÓA TÀI LIỆU</span></div>
        <div className="login-stage">
        <div className="login-lamp">
          <div className="lamp-stem" aria-hidden="true" />
          <div className="lamp-cap" aria-hidden="true" />
          <div className="lamp-bulb" aria-hidden="true"><span /></div>
          <div className="lamp-shade" aria-hidden="true"><span className="lamp-eyes" /><span className="lamp-cheeks" /><span className="lamp-mouth" /></div>
          <div className="lamp-rim" aria-hidden="true" />
          <div className="lamp-glow" aria-hidden="true" />
          <div className="lamp-pool" aria-hidden="true" />
          <button type="button" className="lamp-pull" aria-label={lightOn ? 'Kéo lưỡi để tắt đèn' : 'Kéo lưỡi để bật đèn'} aria-pressed={lightOn}
            style={{ '--pull-distance': `${pullDistance}px`, '--cord-scale': 1 + pullDistance / 91 }}
            onPointerDown={(event) => { if (event.button !== 0) return; pullStart.current = event.clientY; pullDragged.current = false; event.currentTarget.setPointerCapture(event.pointerId); }}
            onPointerMove={(event) => { if (pullStart.current !== null) { const distance = Math.max(0, Math.min(65, event.clientY - pullStart.current)); setPullDistance(distance); if (distance > 8) pullDragged.current = true; } }}
            onPointerUp={(event) => { if (pullStart.current === null) return; if (event.clientY - pullStart.current >= 24) setLightOn((value) => !value); pullStart.current = null; setPullDistance(0); }}
            onPointerCancel={() => { pullStart.current = null; pullDragged.current = true; setPullDistance(0); }}
            onClick={(event) => { if (event.detail === 0 || !pullDragged.current) setLightOn((value) => !value); }}>
            <span className="pull-cord" aria-hidden="true" /><span className="pull-handle" aria-hidden="true" />
          </button>
        </div>
        {lightOn && <p className="lamp-hint" aria-live="polite">Đăng nhập và số hóa đi thôi</p>}
        <form className="login-card" onSubmit={login} aria-hidden={!lightOn} inert={lightOn ? undefined : ""}>
          <span className="login-wordmark"><span /> SỐ HÓA TƯỜNG TRUNG</span>
          <h1>Chào mừng trở lại.</h1>
          <p>Số hóa tài liệu. Kết nối giá trị.</p>
          <label>
            Tài khoản
            <input name="username" autoComplete="username" required value={loginForm.username} onChange={(event) => setLoginForm({ ...loginForm, username: event.target.value })} />
          </label>
          <label>
            Mật khẩu
            <input name="password" autoComplete="current-password" required type="password" value={loginForm.password} onChange={(event) => setLoginForm({ ...loginForm, password: event.target.value })} />
          </label>
          {loginError && <div className="alert danger-alert" role="alert">{loginError}</div>}
          <button className="primary" type="submit" disabled={loginPending}>{loginPending ? 'Đang đăng nhập…' : 'Đăng nhập'}</button>
        </form>
        </div>
        <div className="login-footer">Tường Trung · Giải pháp số hóa tài liệu</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <a className="skip-link" href="#main-content">Bỏ qua điều hướng</a>
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">TT</div>
          <div>
            <strong>Số hóa Tường Trung</strong>
            <span>GIẢI PHÁP SỐ HÓA TÀI LIỆU</span>
          </div>
        </div>
        <nav aria-label="Điều hướng chính">
          <span className="nav-label">KHÔNG GIAN SỐ HÓA</span>
          {['extract', 'catalog', 'ocr', 'account'].map((view) => <button key={view} title={VIEW_COPY[view].label} aria-label={VIEW_COPY[view].label} aria-current={activeView === view ? 'page' : undefined} className={activeView === view ? 'active' : ''} onClick={() => changeView(view)}><NavIcon name={VIEW_COPY[view].icon} /><span>{VIEW_COPY[view].label}</span></button>)}
        </nav>
        <div className="sidebar-note"><span>TƯỜNG TRUNG</span><strong>Số hóa hôm nay.<br />Kết nối ngày mai.</strong><p>Từ trang giấy đến những giá trị có thể tìm thấy, sử dụng và sẻ chia.</p></div>
        <div className="sidebar-user"><span className="user-avatar">{(user.fullName || user.username || 'TT').slice(0, 2).toUpperCase()}</span><div><strong>{user.fullName || user.username}</strong><small>Không gian làm việc</small></div></div>
        <button className="ghost logout" aria-label="Đăng xuất" title="Đăng xuất" onClick={logout}><NavIcon name="logout" /><span>Đăng xuất</span></button>
      </aside>

      <main className="workspace" id="main-content">
        <div className="workspace-topline"><span>TƯỜNG TRUNG <span>/</span> {VIEW_COPY[activeView].label}</span><span>GIẢI PHÁP SỐ HÓA TÀI LIỆU</span></div>
        <header className="workspace-head">
          <div><span className="page-kicker">{VIEW_COPY[activeView].label}</span><h1>{VIEW_COPY[activeView].title}</h1><p>{VIEW_COPY[activeView].description}</p></div>
          <div className="page-emblem" aria-hidden="true"><NavIcon name={VIEW_COPY[activeView].icon} /></div>
        </header>
        {configDirty && activeView !== 'catalog' && <div className="unsaved-banner" role="status"><span><strong>Có thay đổi chưa lưu</strong> · Nhóm và mẫu số hóa chỉ được cập nhật sau khi lưu.</span><button onClick={() => { setCatalogTab(templatesDirty ? 'templates' : 'groups'); setActiveView('catalog'); }}>Về trang chỉnh sửa</button></div>}
        {message && <div className="workspace-feedback" role="status">{message}</div>}
        {(activeView === 'extract' || activeView === 'ocr') && (
        <section className="health-strip">
          <div className={`health-item ${health?.database ? 'ok' : 'bad'}`}>
            <strong>Lưu trữ dữ liệu</strong>
            <span>{health?.database ? 'Đã kết nối' : 'Chưa sẵn sàng'}</span>
          </div>
          <div className={`health-item ${health?.cockpit?.status === 'ok' ? 'ok' : 'warn'}`}>
            <strong>Nhận dạng tài liệu</strong>
            <span>{health?.cockpit?.status === 'ok' ? `${health.cockpit.accounts || 0} tài khoản` : health?.cockpit?.error || 'Đang kiểm tra'}</span>
          </div>
          <div className="health-item">
            <strong>Thư mục nguồn</strong>
            <span title={health?.hostScanRoot || ''}>{health?.hostScanRoot ? `${health.hostScanRoot} -> ${health.scanRoot || '/scan'}` : health?.scanRoot || '/scan'}</span>
          </div>
        </section>

        )}

        {activeView === 'extract' && (
          <div className="pipeline-workspace">
            <section className="pipeline-rail" aria-label="Luồng xử lý hồ sơ">
              <PipelineStep number="1" label="Nguồn hồ sơ" detail={folderPath ? `/scan/${folderPath}` : '/scan'} ready={selectedImageCount > 0} />
              <PipelineStep number="2" label="Mẫu dữ liệu" detail={selectedType?.name || 'Chưa chọn'} ready={Boolean(selectedType && fields.length)} />
              <PipelineStep number="3" label="Nhận dạng tài liệu" detail={health?.cockpit?.status === 'ok' ? 'API sẵn sàng' : 'Chưa kết nối'} ready={health?.cockpit?.status === 'ok'} active={running} />
              <PipelineStep number="4" label="Kết quả Excel" detail={downloadUrl ? 'Đã tạo file' : 'Chờ xử lý'} ready={Boolean(downloadUrl)} />
            </section>

            <div className="extract-grid">
            <section className="panel folder-panel">
              <div className="panel-title">
                <div><span className="eyebrow">Bước 1</span><h2>Chọn nguồn hồ sơ</h2></div>
                <button onClick={() => loadFolder(folderPath)}>Làm mới</button>
              </div>
              <div className="source-location">
                <span>Nguồn đang chọn</span>
                <strong>{folderPath ? `/scan/${folderPath}` : '/scan'}</strong>
              </div>
              <div className="folder-browser">
                <button className="folder-item folder-up" disabled={!folderPath} onClick={() => loadFolder(pathUp(folderPath))}>↑ Thư mục cha</button>
                {(folderInfo?.folders || []).map((folder) => (
                  <button key={folder.relativePath} className="folder-item folder-rich" onClick={() => loadFolder(folder.relativePath)}>
                    <NavIcon name="catalog" /><strong>{folder.name}</strong>
                    <span>{folder.imageCount} tệp{folder.pdfCount ? `, ${folder.pdfCount} PDF` : ''}, {folder.errorCount} lỗi{folder.excelFiles?.length ? ', có Excel' : ''}</span>
                  </button>
                ))}
              </div>
              {!folderInfo?.folders?.length && <div className="source-empty"><NavIcon name="scan" /><strong>{folderInfo ? 'Không có thư mục con' : 'Đang đọc nguồn tài liệu…'}</strong><p>{folderInfo ? 'Chọn thư mục cha để tìm nguồn hồ sơ khác, hoặc kiểm tra tài liệu trong thư mục hiện tại.' : 'Danh sách hồ sơ sẽ xuất hiện tại đây.'}</p></div>}
              <div className="breadcrumb">
                {breadcrumbParts().map((part, index) => (
                  <button key={part.path || 'root'} onClick={() => loadFolder(part.path)}>
                    {part.label}{index < breadcrumbParts().length - 1 ? ' /' : ''}
                  </button>
                ))}
              </div>
              <div className="file-summary">
                <div>
                  <strong>Excel hiện có</strong>
                  <span>
                    {folderInfo?.excelFiles?.length ? folderInfo.excelFiles.map((file) => (
                      <a className="mini-link excel-chip" key={file} href={`/api/pipeline/folders/excel?path=${encodeURIComponent(folderPath)}&file=${encodeURIComponent(file)}`}>
                        {file}
                      </a>
                    )) : 'Chưa có'}
                  </span>
                </div>
                <div>
                  <strong>Tệp lỗi</strong>
                  <span>{folderInfo?.errorCount || 0}</span>
                </div>
              </div>
              <details className="file-disclosure"><summary>Tệp trong thư mục <span>{folderInfo?.images?.length || 0} tệp</span></summary><div className="image-list">
                {(folderInfo?.images || []).slice(0, 80).map((name) => (
                  <code className={name.toUpperCase().startsWith('ERROR_') ? 'error-file' : ''} key={name}>{name}</code>
                ))}
                {(folderInfo?.images || []).length > 80 && <span className="more-files">Còn {(folderInfo.images.length - 80)} ảnh khác</span>}
              </div></details>
            </section>

            <section className="panel run-panel">
              <div className="panel-title">
                <div><span className="eyebrow">Bước 2</span><h2>Chọn mẫu và số hóa tài liệu</h2></div>
                {downloadUrl && <a className="button" href={downloadUrl}>Tải Excel</a>}
              </div>
              <label>
                Loại tài liệu
                <CartoonSelect value={selectedType?.id || ''} onChange={(event) => setSelectedTypeId(event.target.value)}>
                  {config.document_types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                </CartoonSelect>
              </label>
              <label htmlFor="layout-mode">
                Cấu trúc bên trong nguồn
                <CartoonSelect id="layout-mode" value={layoutMode} onChange={(event) => setLayoutMode(event.target.value)} disabled={running}>
                  <option value="direct">Nguồn → Hồ sơ → Ảnh</option>
                  <option value="person_folders">Nguồn → Hồ sơ → Người → Ảnh</option>
                </CartoonSelect>
                <span className="field-help">
                  {layoutMode === 'person_folders'
                    ? `Mỗi thư mục cấp một là một hồ sơ; bên trong là các thư mục người. Tìm thấy ${selectedImageCount} tệp và sẽ thêm cột “Tên người” vào Excel.`
                    : `Mỗi thư mục cấp một là một hồ sơ và chứa ảnh cần trích xuất. Tìm thấy ${selectedImageCount} tệp.`}
                </span>
              </label>
              <details className="inline-ocr-settings advanced-settings"><summary><span><NavIcon name="settings" />Tùy chỉnh nhận dạng</span><small>Mô hình, tốc độ và chất lượng ảnh</small></summary>
                <div className="section-heading">
                  <div><span className="eyebrow">Cài đặt OCR</span><strong>Thông số cho lần chạy này</strong></div>
                  <button className="compact" onClick={saveOcrSettings} disabled={!ocrSettings || ocrSaving}>{ocrSaving ? 'Đang lưu…' : 'Áp dụng'}</button>
                </div>
                {ocrSettings ? (
                  <div className="ocr-settings-grid">
                    <label>
                      Tài khoản nhận dạng
                      <CartoonSelect value={ocrSettings.accountId || ''} onChange={(event) => setOcrSettings({ ...ocrSettings, accountId: event.target.value, model: '' })} disabled={running}>
                        <optgroup label="Codex">
                          {ocrAccounts.filter((account) => account.provider === 'codex' && account.enabled !== false).map((account) => <option key={account.key} value={account.key}>{account.email}</option>)}
                        </optgroup>
                        <optgroup label="Antigravity">
                          {ocrAccounts.filter((account) => account.provider === 'antigravity' && account.enabled !== false).map((account) => <option key={account.key} value={account.key}>{account.email}</option>)}
                        </optgroup>
                        <optgroup label="Claude API">
                          {ocrAccounts.filter((account) => account.provider === 'claude' && account.enabled !== false).map((account) => <option key={account.key} value={account.key}>{account.email}</option>)}
                        </optgroup>
                        <optgroup label="Gemini API">
                          {ocrAccounts.filter((account) => account.provider === 'gemini' && account.enabled !== false).map((account) => <option key={account.key} value={account.key}>{account.email}</option>)}
                        </optgroup>
                        <optgroup label="Cockpit Local API">
                          {ocrAccounts.filter((account) => account.provider === 'cockpit' && account.enabled !== false).map((account) => <option key={account.key} value={account.key}>{account.email}</option>)}
                        </optgroup>
                      </CartoonSelect>
                    </label>
                    <label className="checkbox-setting">
                      <input type="checkbox" checked={ocrSettings.rotateAccounts === true} onChange={(event) => setOcrSettings({ ...ocrSettings, rotateAccounts: event.target.checked })} disabled={running} />
                      <span><strong>Tự chuyển tài khoản</strong><small>Dùng tài khoản cùng nhà cung cấp khi gặp lỗi hoặc hết hạn mức.</small></span>
                    </label>
                    <label>
                      Mô hình AI
                      <CartoonSelect value={ocrSettings.model || ''} onChange={(event) => setOcrSettings({ ...ocrSettings, model: event.target.value })} disabled={running}>
                        {!ocrModels.length && <option value="">Chưa có mô hình khả dụng</option>}
                        {ocrModels.map((model) => <option key={`${model.quotaGroupId || ''}:${model.id}`} value={model.id}>{model.displayName || model.id}</option>)}
                      </CartoonSelect>
                      {ocrSettings.accountId?.startsWith('cockpit:') && <small className="field-help">Cockpit Tools quản lý tài khoản và quota. <a href="/accounts?provider=cockpit">Cấu hình kết nối</a></small>}
                      {ocrSettings.accountId?.startsWith('gemini:') && <small className="field-help">Gateway xếp hàng theo RPM/TPM/RPD đã cấu hình trong tab Gemini API. Tăng số luồng không vượt được giới hạn này.</small>}
                    </label>
                    <label>
                      Mức độ suy luận
                      <CartoonSelect value={ocrReasoningEfforts.length ? ocrSettings.reasoningEffort || 'low' : ''} onChange={(event) => setOcrSettings({ ...ocrSettings, reasoningEffort: event.target.value })} disabled={running || !ocrReasoningEfforts.length}>
                        {!ocrReasoningEfforts.length && <option value="">{ocrSettings.accountId?.startsWith('gemini:') ? 'Mặc định của Gemini/Gemma' : ocrSettings.accountId?.startsWith('cockpit:') ? 'Mặc định của Cockpit Tools' : 'Mặc định của Claude'}</option>}
                        {ocrReasoningEfforts.map((effort) => <option key={effort} value={effort}>{effort}</option>)}
                      </CartoonSelect>
                    </label>
                    {ocrSettings.accountId?.startsWith('claude:') && <label>
                      Prompt caching Claude
                      <CartoonSelect value={ocrSettings.claudeCacheTtl || '5m'} onChange={(event) => setOcrSettings({ ...ocrSettings, claudeCacheTtl: event.target.value })} disabled={running}>
                        <option value="5m">5 phút · phù hợp chạy liên tục</option>
                        <option value="1h">1 giờ · phí ghi cache cao hơn</option>
                        <option value="off">Tắt cache</option>
                      </CartoonSelect>
                      <small className="field-help">Tái sử dụng prompt và cấu trúc trường. Prompt cần đạt ngưỡng token của model để có cache hit.</small>
                    </label>}
                    <label>
                      Cách chạy
                      <CartoonSelect value={ocrSettings.concurrency || 1} onChange={(event) => setOcrSettings({ ...ocrSettings, concurrency: Number(event.target.value) })} disabled={running}>
                        {![1, 2, 4, 8, 16].includes(Number(ocrSettings.concurrency)) && <option value={ocrSettings.concurrency}>Song song · {ocrSettings.concurrency} tệp</option>}
                        <option value="1">Tuần tự</option>
                        <option value="2">Song song · 2 tệp</option>
                        <option value="4">Song song · 4 tệp</option>
                        <option value="8">Song song · 8 tệp</option>
                        <option value="16">Song song · 16 tệp</option>
                      </CartoonSelect>
                    </label>
                    <label>
                      Số lần thử lại
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={ocrSettings.retries ?? ''}
                        onChange={(event) => setOcrSettings({ ...ocrSettings, retries: event.target.value })}
                        onBlur={() => setOcrSettings((current) => ({ ...current, retries: clampNumber(current.retries, 1, 10, 3) }))}
                        disabled={running}
                      />
                    </label>
                    <label>
                      Chiều rộng ảnh tối đa (px)
                      <input
                        type="number"
                        min="320"
                        max="10000"
                        step="100"
                        value={ocrSettings.maxImageWidth ?? ''}
                        onChange={(event) => setOcrSettings({ ...ocrSettings, maxImageWidth: event.target.value })}
                        onBlur={() => setOcrSettings((current) => ({ ...current, maxImageWidth: clampNumber(current.maxImageWidth, 320, 10000, 1200) }))}
                        disabled={running}
                      />
                    </label>
                  </div>
                ) : <div className="empty-state">Đang tải cấu hình nhận dạng…</div>}
              </details>
              <div className="method-banner">
                <strong>Dịch vụ nhận dạng tài liệu · {health?.cockpit?.status === 'ok' ? 'Đã kết nối' : 'Chưa sẵn sàng'}</strong>
                <span>Tài liệu được số hóa bằng tài khoản và mô hình đã chọn ở trên.</span>
              </div>
              <label>
                Trường Từ số để tính Đến số
                <CartoonSelect value={tuSoFieldId} onChange={(event) => setTuSoFieldId(event.target.value)}>
                  <option value="">Không tính Đến số</option>
                  {fields.filter((field) => field.field_type !== 'document_page_count').map((field) => <option key={field.id} value={field.id}>{field.name}</option>)}
                </CartoonSelect>
              </label>
              <div className="template-summary">
                <strong>{fields.length} trường đầu ra</strong>
                <span>{selectedType?.system_prompt && selectedType?.user_prompt ? 'Đã có hướng dẫn nhận dạng' : 'Chưa đủ hướng dẫn nhận dạng'}</span>
                <button className="text-button" onClick={() => changeView('catalog')}>Chỉnh mẫu dữ liệu</button>
              </div>
              <div className="action-row">
                <button onClick={exportAntigravityTask} disabled={!selectedType || !fields.length || !folderInfo || selectedImageCount === 0}>Xuất cấu hình cho Antigravity</button>
              </div>
              <p className="run-guidance">Xuất mẫu và nguồn đang chọn{templatesDirty ? ', gồm thay đổi mẫu chưa lưu' : ''} để OCR trong Antigravity. Cần truy cập được thư mục ảnh trên máy chạy agent.</p>
              <div className="progress-block">
                <div className="progress-head">
                  <strong>{progressPercent}%</strong>
                  <span>{currentImage}/{totalImages} tệp</span>
                </div>
                <div className="progress-track"><div style={{ width: `${progressPercent}%` }} /></div>
              </div>
              {pausedJob && <div className="result-banner paused" role="status" aria-live="polite">
                <strong>Tiến trình đang tạm dừng</strong>
                <p>{pausedJob.message}</p>
                <p>Ảnh gặp quota và ảnh chưa xử lý được giữ nguyên. Kết quả đã đọc được lưu; Tiếp tục sẽ chỉ chạy phần còn lại.</p>
                {pausedJob.retryAt && <p>Thời điểm thử lại sớm nhất cho kết nối hiện tại: {new Date(pausedJob.retryAt).toLocaleString('vi-VN')}.</p>}
              </div>}
              <div className="action-row">
                {pausedJob && <button className="primary" disabled={resuming} onClick={resumeJob}>{resuming ? 'Đang tiếp tục…' : 'Tiếp tục phần còn lại'}</button>}
                {pausedJob && <button className="danger" disabled={resuming} onClick={stopJob}>Kết thúc tiến trình</button>}
                <button className="primary" disabled={running || !!pausedJob || !selectedType || !fields.length || selectedImageCount === 0 || health?.cockpit?.status !== 'ok'} onClick={startJob}>Bắt đầu số hóa</button>
                {running && <button className="danger" onClick={stopJob}>Dừng</button>}
              </div>
              {!running && (!selectedType || !fields.length || selectedImageCount === 0 || health?.cockpit?.status !== 'ok') && <p className="run-guidance">{!selectedType || !fields.length ? 'Chọn mẫu có trường dữ liệu để bắt đầu.' : selectedImageCount === 0 ? 'Chọn nguồn có tài liệu phù hợp với cấu trúc thư mục ở trên.' : 'Kết nối dịch vụ nhận dạng trong mục Kết nối AI để bắt đầu.'}</p>}
            </section>

            <section className="panel event-panel pipeline-monitor">
              <div className="panel-title">
                <div><span className="eyebrow">Bước 3–4</span><h2>Xử lý và xuất kết quả</h2></div>
                <span>{running ? 'Đang xử lý' : pausedJob ? 'Đang tạm dừng' : completeEvent ? 'Đã hoàn tất' : 'Chưa chạy'}</span>
              </div>
              <div className="metric-row">
                <Metric label="Tổng tệp" value={totalImages || 0} />
                <Metric label="Đã xử lý" value={completeEvent?.payload?.processedCount || progressEvent?.payload?.processedCount || 0} />
                <Metric label="Lỗi" value={completeEvent?.payload?.errorCount || progressEvent?.payload?.errorCount || 0} tone="bad" />
              </div>
              <ProcessingSummary events={events} downloadUrl={downloadUrl} />
            </section>
            </div>
          </div>
        )}

        {activeView === 'catalog' && (
          <div className="catalog-grid catalog-organized">
            <div className="catalog-topline">
              <nav className="catalog-tabs" aria-label="Quản lý mẫu và nhóm">
                <button aria-current={catalogTab === 'templates' ? 'page' : undefined} className={catalogTab === 'templates' ? 'primary' : ''} onClick={() => setCatalogTab('templates')}>Mẫu số hóa <span>{config.document_types.length}</span>{templatesDirty && <span aria-label="Có thay đổi chưa lưu">•</span>}</button>
                <button aria-current={catalogTab === 'groups' ? 'page' : undefined} className={catalogTab === 'groups' ? 'primary' : ''} onClick={() => setCatalogTab('groups')}>Nhóm tài liệu <span>{config.groups.length}</span>{groupsDirty && <span aria-label="Có thay đổi chưa lưu">•</span>}</button>
              </nav>
              <p>{catalogTab === 'templates' ? 'Thiết lập dữ liệu cần lấy cho từng loại tài liệu.' : 'Tổ chức các mẫu số hóa thành những nhóm dễ tìm.'}</p>
            </div>
            {catalogTab === 'templates' && <>

            <section className="panel catalog-commandbar">
              <label className="catalog-type-picker">
                <span>Chọn mẫu để chỉnh sửa</span>
                <CartoonSelect value={selectedType?.id || ''} onChange={(event) => setSelectedTypeId(event.target.value)}>
                  {!config.document_types.length && <option value="">Chưa có loại tài liệu</option>}
                  {config.document_types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                </CartoonSelect>
              </label>
              <div className="catalog-actions">
                <span className="catalog-save-status" role="status">{templatesDirty ? 'Mẫu có thay đổi chưa lưu' : 'Mẫu đã được lưu'}</span>
                <button className="primary" onClick={() => saveConfig('templates')} disabled={configSaving || !templatesDirty}>{configSaving ? 'Đang lưu…' : 'Lưu mẫu'}</button>
                <button onClick={addType}>Thêm mẫu</button>
                <details className="catalog-more"><summary>Thao tác khác</summary><div>
                  <button onClick={(event) => { event.currentTarget.closest('details').open = false; duplicateType(); }} disabled={!selectedType}>Nhân bản mẫu</button>
                  <button className="danger" onClick={(event) => { event.currentTarget.closest('details').open = false; removeType(); }} disabled={!selectedType}>Xóa mẫu</button>
                </div></details>
              </div>
            </section>

            <section className="panel catalog-editor">
              <div className="panel-title">
                <div><span className="page-kicker">BƯỚC 1</span><h2>Thông tin cơ bản</h2><p>Đặt tên mẫu, phân nhóm và chọn số trang cần đọc.</p></div>
              </div>
              {selectedType && (
                <>

                  <div className="form-grid">
                    <label>
                      Tên mẫu
                      <input value={selectedType.name} onChange={(event) => updateType({ name: event.target.value })} />
                    </label>
                    <label>
                      Nhóm của mẫu
                      <CartoonSelect value={selectedType.group_id || ''} onChange={(event) => updateType({ group_id: event.target.value })}>
                        <option value="">Chưa phân nhóm</option>
                        {config.groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                      </CartoonSelect>
                    </label>
                    <label className="pdf-pages-field" htmlFor="pdf-extract-pages">
                      Số trang đầu trích xuất từ PDF
                      <input
                        id="pdf-extract-pages"
                        aria-describedby="pdf-pages-help"
                        type="number"
                        min="1"
                        max="20"
                        value={selectedType.pdf_extract_pages || 1}
                        onChange={(event) => updateType({ pdf_extract_pages: Math.max(1, Math.min(20, Number(event.target.value) || 1)) })}
                      />
                      <span className="field-help" id="pdf-pages-help">Đọc từ trang đầu tiên, tối đa 20 trang mỗi PDF.</span>
                    </label>
                  </div>

                </>
              )}
              {!selectedType && <div className="empty-state">Thêm một loại tài liệu để bắt đầu cấu hình.</div>}
            </section>



            <section className="panel wide catalog-fields">
              <div className="panel-title">
                <div><span className="page-kicker">BƯỚC 2</span><h2>Trường dữ liệu</h2><p>{fields.length} cột sẽ được xuất ra Excel.</p></div>
                <button onClick={addField} disabled={!selectedType}>Thêm trường</button>
              </div>
              <div className="field-table">
                {fields.map((field, index) => (
                  <div className="field-table-row" key={field.id || index}>
                    <div className="field-index" aria-label={`Trường số ${index + 1}`}>{index + 1}</div>
                    <label className="field-name">Tên trường<input value={field.name} onChange={(event) => updateField(index, { name: event.target.value })} /></label>
                    <label className="field-type">Kiểu dữ liệu<CartoonSelect value={field.field_type || 'text'} onChange={(event) => updateField(index, { field_type: event.target.value })}>
                        <option value="text">Văn bản</option>
                        <option value="number">Số</option>
                        <option value="date">Ngày tháng</option>
                        <option value="long_text">Văn bản dài</option>
                        <option value="document_page_count">Số trang (tự động)</option>
                      </CartoonSelect></label>
                    <label className="field-guidance">Hướng dẫn trích xuất<textarea
                        className="field-instruction-input"
                        rows="3"
                        value={field.instruction || ''}
                        placeholder={'Ví dụ:\n- Lấy phần số trước dấu /\n- Không có thì để trống'}
                        onChange={(event) => updateField(index, { instruction: event.target.value })}
                      /></label>
                    <div className="field-actions" aria-label={`Sắp xếp trường ${field.name || index + 1}`}>
                      <button onClick={() => moveField(index, -1)} disabled={index === 0}>↑ Lên</button>
                      <button onClick={() => moveField(index, 1)} disabled={index === fields.length - 1}>↓ Xuống</button>
                      <button className="danger" onClick={() => removeField(index)}>Xóa</button>
                    </div>
                  </div>
                ))}
                {!fields.length && <div className="empty-state">Chưa có trường dữ liệu cho loại tài liệu này.</div>}
              </div>
            </section>
            {selectedType && <section className="panel catalog-advanced">
                  <details className="prompt-disclosure"><summary>Hướng dẫn nhận dạng nâng cao <span>Quy tắc đọc tài liệu</span></summary>
                  <div className="prompt-editor-grid">
                    <label>
                      Quy tắc nhận dạng
                      <textarea rows="12" value={selectedType.system_prompt || ''} onChange={(event) => updateType({ system_prompt: event.target.value })} />
                      <span className="field-help">Quy định cách đọc và kiểm tra thông tin trên tài liệu.</span>
                    </label>
                    <label>
                      Yêu cầu trích xuất
                      <textarea rows="12" value={selectedType.user_prompt || ''} onChange={(event) => updateType({ user_prompt: event.target.value })} />
                      <span className="field-help">Dùng <code>{'{{field_instructions}}'}</code> để chèn tên, kiểu và hướng dẫn riêng của các trường dữ liệu.</span>
                    </label>
                  </div></details>
            </section>}
            {selectedType && <PromptFlowCard selectedType={selectedType} fields={fields} />}
            </>}
            {catalogTab === 'groups' && <>
            <section className="panel catalog-groups">
              <div className="panel-title">
                <div><h2>Nhóm tài liệu</h2><p>{config.groups.length} nhóm phân loại</p></div>
                <div className="catalog-actions">
                  <span className="catalog-save-status" role="status">{groupsDirty ? 'Nhóm có thay đổi chưa lưu' : 'Nhóm đã được lưu'}</span>
                  <button className="primary" onClick={() => saveConfig('groups')} disabled={configSaving || !groupsDirty}>{configSaving ? 'Đang lưu…' : 'Lưu nhóm'}</button>
                  <button onClick={addGroup}>Thêm nhóm</button>
                </div>
              </div>
              <label>Chọn nhóm để chỉnh sửa
                <CartoonSelect value={selectedGroup?.id || ''} onChange={(event) => setSelectedGroupId(event.target.value)}>
                  {!config.groups.length && <option value="">Chưa có nhóm</option>}
                  {config.groups.map((group) => <option key={group.id} value={group.id}>{group.name || 'Nhóm chưa đặt tên'}</option>)}
                </CartoonSelect>
              </label>
              {selectedGroup ? <div className="single-group-editor" key={selectedGroup.id}>

                <label>Tên nhóm<input value={selectedGroup.name} onChange={(event) => updateGroup(config.groups.findIndex((group) => group.id === selectedGroup.id), { name: event.target.value })} /></label>
                <label>Mô tả<textarea rows="3" value={selectedGroup.description || ''} placeholder="Nhóm này dùng cho tài liệu nào?" onChange={(event) => updateGroup(config.groups.findIndex((group) => group.id === selectedGroup.id), { description: event.target.value })} /></label>
                <div className="group-members"><h3>Mẫu thuộc nhóm này</h3><p className="field-help">Chọn một mẫu để mở cấu hình.</p>
                  <div className="group-member-list">{config.document_types.filter(type => type.group_id === selectedGroup.id).map(type => <button key={type.id} onClick={() => { setSelectedTypeId(type.id); setCatalogTab('templates'); }}>{type.name}<span aria-hidden="true">→</span></button>)}</div>
                  {!config.document_types.some(type => type.group_id === selectedGroup.id) && <p className="empty-state">Chưa có mẫu. Bạn có thể gán nhóm trong thông tin cơ bản của mẫu.</p>}
                </div>
                <button className="danger" onClick={() => removeGroup(config.groups.findIndex((group) => group.id === selectedGroup.id))}>Xóa nhóm này</button>

              </div> : <div className="empty-state">Thêm nhóm đầu tiên để phân loại các mẫu số hóa.</div>}

            </section>

            </>}

          </div>
        )}

        {activeView === 'account' && (
          <div className="account-layout">
          <section className="identity-card"><span className="page-kicker">HỒ SƠ CÁ NHÂN</span><div className="identity-avatar"><NavIcon name="user" /></div><h2>{user.fullName || user.username}</h2><p>@{user.username}</p><div className="identity-divider" /><strong>Số hóa Tường Trung</strong><p>Một tài khoản cho toàn bộ quy trình số hóa tài liệu.</p></section>
          <section className="panel account-panel">
            <div className="panel-title">
              <h2>Bảo mật tài khoản</h2>
              <span>{user.username}</span>
            </div>
            <p className="section-intro">Cập nhật mật khẩu để bảo vệ không gian số hóa của bạn.</p>
            <form className="password-form" onSubmit={changePassword}>
              <label>
                Mật khẩu hiện tại
                <input type="password" autoComplete="current-password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })} />
              </label>
              <label>
                Mật khẩu mới
                <input type="password" autoComplete="new-password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })} />
              </label>
              <label>
                Nhập lại mật khẩu mới
                <input type="password" autoComplete="new-password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })} />
              </label>
              <button className="primary" type="submit">Đổi mật khẩu</button>
            </form>
          </section></div>
        )}

        {activeView === 'ocr' && (
          <section className="ocr-console" aria-label="Cấu hình nhận dạng">
            <iframe src="/accounts?embed=1" title="Cấu hình nhận dạng" />
          </section>
        )}
      </main>
      {confirmation && <ConfirmationDialog message={confirmation} onFinish={finishConfirmation} />}
    </div>
  );
}

function ConfirmationDialog({ message, onFinish }) {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    dialog.showModal();
    cancelRef.current.focus();
    return () => { dialog.close(); previous?.focus(); };
  }, []);
  return <dialog ref={dialogRef} className="tt-dialog" aria-labelledby="confirmation-title" aria-describedby="confirmation-message" onCancel={(event) => { event.preventDefault(); onFinish(false); }}>
    <div className="dialog-sticker" aria-hidden="true">!</div>
    <span className="dialog-kicker">SỐ HÓA TƯỜNG TRUNG</span>
    <h2 id="confirmation-title">Khoan một chút nhé!</h2>
    <p id="confirmation-message">{message}</p>
    <div className="dialog-actions"><button ref={cancelRef} onClick={() => onFinish(false)}>Quay lại</button><button className="primary" onClick={() => onFinish(true)}>Tiếp tục</button></div>
  </dialog>;
}

function Metric({ label, value, tone = '' }) {
  return (
    <div className={`metric ${tone}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function NavIcon({ name }) {
  const paths = {
    scan: <><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/><path d="M7 12h10M7 9h10M7 15h7"/></>,
    catalog: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/></>,
    user: <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.1A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.6-1H3v-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6V3h4v.1A1.7 1.7 0 0 0 15 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.6 1h.1v4H21a1.7 1.7 0 0 0-1.6 1Z"/></>,
    logout: <><path d="M10 17l5-5-5-5M15 12H3"/><path d="M14 3h5a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-5"/></>,
  };
  return <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function PipelineStep({ number, label, detail, ready = false, active = false }) {
  return (
    <div className={`pipeline-step ${ready ? 'ready' : ''} ${active ? 'active' : ''}`}>
      <span className="step-number">{ready ? '✓' : number}</span>
      <div>
        <strong>{label}</strong>
        <span title={detail}>{detail}</span>
      </div>
    </div>
  );
}

function ProcessingSummary({ events, downloadUrl }) {
  const errors = events.filter((item) => item.type === 'imageError').slice(0, 20);
  const terminal = events.find((item) => ['complete', 'stopped', 'error', 'paused'].includes(item.type));
  if (!events.length) return <div className="empty-state">Chọn nguồn và mẫu dữ liệu, sau đó bấm “Bắt đầu số hóa”.</div>;
  return (
    <div className="processing-summary">
      <div className={`result-banner ${terminal?.type || 'running'}`}>
        <strong>{terminal ? eventLabel(terminal.type) : 'Đang số hóa tài liệu'}</strong>
        <span>{downloadUrl ? <a className="button primary compact" href={downloadUrl}>Tải file Excel</a> : 'Kết quả Excel sẽ xuất hiện tại đây khi hoàn tất.'}</span>
      </div>
      {errors.length > 0 && (
        <div className="error-summary">
          <strong>Lỗi gần nhất ({errors.length}{errors.length === 20 ? '+' : ''})</strong>
          {errors.map((item, index) => <code key={`${item.at}-${index}`}>{item.payload.errorFileName || item.payload.imageName}: {item.payload.error || 'Không xử lý được'}</code>)}
        </div>
      )}
    </div>
  );
}

function PromptFlowCard({ selectedType, fields }) {
  const ocrFields = fields.filter((field) => field.field_type !== 'document_page_count');
  const previewFields = ocrFields.length ? ocrFields : [{ name: 'Tên trường', field_type: 'text', instruction: 'Hướng dẫn riêng của trường\nCó thể nhập nhiều dòng' }];
  const fieldRules = previewFields.map(formatFieldRule).join('\n');
  const systemPrompt = renderPromptPreview(selectedType?.system_prompt || DEFAULT_SYSTEM_PROMPT, fieldRules);
  const userPrompt = renderPromptPreview(selectedType?.user_prompt || DEFAULT_USER_PROMPT, fieldRules);
  const combinedPrompt = `${systemPrompt}\n\n${userPrompt}`;
  return (
    <details className="panel prompt-flow-card">
      <summary className="panel-title">
        <div>
          <h2>Xem trước hướng dẫn nhận dạng</h2>
          <p>Kiểm tra toàn bộ hướng dẫn áp dụng cho mẫu số hóa này.</p>
        </div>

      </summary>
      <div className="prompt-flow-content">
        <div className="prompt-note">
          Đây là hướng dẫn gửi cùng ảnh để nhận dạng nội dung. Biến <code>{'{{field_instructions}}'}</code> đã được thay bằng hướng dẫn chi tiết của từng trường; trường “Số trang tài liệu” do hệ thống tự đếm nên không gửi đến mô hình AI.
        </div>
        <div className="prompt-map prompt-map-single">
          <div>
            <strong>Hướng dẫn hoàn chỉnh</strong>
            <pre><code>{combinedPrompt}</code></pre>
          </div>
        </div>
      </div>
    </details>
  );
}

function renderPromptPreview(template, fieldInstructions) {
  return String(template || '').replace(/\{\{field_instructions\}\}/g, fieldInstructions).trim();
}

function clampNumber(value, min, max, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || String(value).trim() === '') return fallback;
  return Math.max(min, Math.min(max, parsed));
}

function formatFieldRule(field) {
  const fieldType = field.field_type || 'text';
  const typeRule = {
    text: 'văn bản',
    number: 'số',
    date: 'ngày tháng',
    long_text: 'văn bản dài',
  }[fieldType] || fieldType;
  const instruction = String(field.instruction || '').trim();
  if (!instruction) return `- "${field.name}" [${typeRule}]`;
  return `- "${field.name}" [${typeRule}]:\n${indentMultiline(instruction)}`;
}

function indentMultiline(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `  + ${line}`)
    .join('\n');
}

function eventLabel(type) {
  return {
    start: 'Bắt đầu',
    progress: 'Tiến trình',
    imageSuccess: 'Thành công',
    imageError: 'Lỗi ảnh',
    excelSaved: 'Lưu Excel',
    complete: 'Hoàn tất',
    stopped: 'Đã dừng',
    paused: 'Tạm dừng — tiến trình đã được lưu',
    error: 'Lỗi',
  }[type] || type;
}

function joinPath(base, child) {
  return [base, child].filter(Boolean).join('/').replace(/\/+/g, '/');
}

function pathUp(value) {
  const parts = String(value || '').split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
}

function methodLabel(value) {
  return 'Nhận dạng tài liệu';
}

function methodDescription(value) {
  return 'Nhận dạng nội dung tài liệu theo mẫu đã chọn, theo dõi tiến độ số hóa và xuất kết quả ra Excel.';
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('vi-VN');
}

export default App;
